/**
 * Globale, gedrosselte Warteschlange für Bild-Downloads.
 *
 * Warum: Der Nhost-Storage-Endpoint ist pro Projekt rate-limitiert. Sobald eine Seite
 * viele noch nicht CDN-gecachte Bilder gleichzeitig anfragt (die Mediathek und der
 * Bild-Picker im Content-Bereich rendern potenziell hunderte auf einmal), antwortet das
 * Ingress mit 502/503 — und zwar für *alle* Requests des Bursts, nicht nur für einzelne.
 * Gemessen: sequentiell mit Abstand laden dieselben URLs zuverlässig mit 200, im
 * Parallel-Burst fallen ~50 % aus.
 *
 * Deshalb zwei Mechanismen:
 *  1. Harte Obergrenze gleichzeitiger Requests + Mindestabstand zwischen Dispatches.
 *  2. Ein *globaler* Cooldown: Weil das Limit projektweit greift, bedeutet ein Fehler,
 *     dass gerade auch alle Nachbar-Requests scheitern. Statt sofort zu wiederholen
 *     (was die Sperre nur verlängert) pausiert die ganze Queue mit exponentiellem
 *     Backoff und läuft nach dem ersten Erfolg wieder hoch.
 */

/** Wie viele Bild-Requests höchstens gleichzeitig unterwegs sind. */
const MAX_CONCURRENT = 3
/** Mindestabstand zwischen zwei Dispatches, damit kein Mikro-Burst entsteht. */
const MIN_DISPATCH_GAP_MS = 90
/** Versuche pro URL, bevor auf das untransformierte Original zurückgefallen wird. */
const MAX_ATTEMPTS = 4
/** Backoff-Basis und Deckel für den globalen Cooldown nach einem Fehler. */
const COOLDOWN_BASE_MS = 1200
const COOLDOWN_MAX_MS = 20000

type Task = {
  /** URL, die aktuell versucht wird (kann auf das Original zurückfallen). */
  url: string
  /** Ursprünglich angefragte URL — Cache-Key, bleibt über Fallbacks hinweg stabil. */
  key: string
  attempts: number
  triedOriginal: boolean
  signal?: AbortSignal
  resolve: (url: string) => void
  reject: (err: Error) => void
}

const queue: Task[] = []
/** URLs, die in dieser Session schon erfolgreich geladen wurden -> Browser-Cache-Treffer. */
const succeeded = new Map<string, string>()
/** Laufende Ladevorgänge, damit dieselbe URL nicht mehrfach in der Queue landet. */
const inProgress = new Map<string, Promise<string>>()

let inFlight = 0
let lastDispatchAt = 0
let consecutiveFailures = 0
let pausedUntil = 0
let lastFailureAt = 0
let pumpTimer: number | null = null

/**
 * Nach dieser Ruhephase ohne Fehler gilt das Rate-Limit als abgeklungen und der Backoff
 * startet wieder bei null. Sonst hielte eine alte Fehlerserie den Cooldown dauerhaft
 * hoch, wenn danach nur rohe <img>-Tags laden — die melden keinen Erfolg zurück.
 */
const FAILURE_DECAY_MS = 30000

/** Erhöht die Fehlerserie, verlängert den globalen Cooldown und liefert dessen Dauer. */
function registerFailure(): number {
  const now = Date.now()
  if (now - lastFailureAt > FAILURE_DECAY_MS) consecutiveFailures = 0
  lastFailureAt = now
  consecutiveFailures++
  const cooldown = Math.min(COOLDOWN_BASE_MS * 2 ** (consecutiveFailures - 1), COOLDOWN_MAX_MS)
  const wait = jitter(cooldown)
  pausedUntil = Math.max(pausedUntil, now + wait)
  return wait
}

function jitter(ms: number): number {
  return ms * (0.75 + Math.random() * 0.5)
}

function schedulePump(delayMs: number): void {
  if (pumpTimer !== null) return
  pumpTimer = window.setTimeout(
    () => {
      pumpTimer = null
      pump()
    },
    Math.max(0, delayMs)
  )
}

function pump(): void {
  while (queue.length > 0 && inFlight < MAX_CONCURRENT) {
    const now = Date.now()
    if (now < pausedUntil) {
      schedulePump(pausedUntil - now)
      return
    }
    const gap = lastDispatchAt + MIN_DISPATCH_GAP_MS - now
    if (gap > 0) {
      schedulePump(gap)
      return
    }

    const task = queue.shift()!
    if (task.signal?.aborted) {
      inProgress.delete(task.key)
      task.reject(new Error('aborted'))
      continue
    }
    lastDispatchAt = now
    inFlight++
    run(task)
  }
}

function onSuccess(task: Task): void {
  inFlight--
  consecutiveFailures = 0
  pausedUntil = 0
  succeeded.set(task.key, task.url)
  inProgress.delete(task.key)
  task.resolve(task.url)
  pump()
}

function onFailure(task: Task): void {
  inFlight--

  // Das Rate-Limit gilt projektweit: ein Fehler heißt, dass gerade auch alle anderen
  // Requests abgewiesen werden. Also die komplette Queue pausieren statt nur diesen Task.
  registerFailure()

  if (task.signal?.aborted) {
    inProgress.delete(task.key)
    task.reject(new Error('aborted'))
    pump()
    return
  }

  task.attempts++
  if (task.attempts < MAX_ATTEMPTS) {
    queue.push(task)
  } else if (!task.triedOriginal && task.url.includes('?')) {
    // Erst wenn die Transform-URL endgültig aufgibt: das untransformierte Original.
    // Das deckt Formate ab, die imgproxy gar nicht verarbeiten kann (animierte GIFs, SVG).
    task.triedOriginal = true
    task.attempts = 0
    task.url = task.url.split('?')[0]
    queue.push(task)
  } else {
    inProgress.delete(task.key)
    task.reject(new Error(`image failed to load: ${task.key}`))
  }
  pump()
}

function run(task: Task): void {
  const img = new Image()
  img.decoding = 'async'
  let done = false
  img.onload = () => {
    if (done) return
    done = true
    onSuccess(task)
  }
  img.onerror = () => {
    if (done) return
    done = true
    onFailure(task)
  }
  img.src = task.url
}

/**
 * Lädt `url` durch die gedrosselte Queue vor und liefert die URL, die tatsächlich
 * funktioniert hat (die Transform-URL oder das Original als Fallback). Danach ist das
 * Bild im Browser-Cache, ein `<img src=...>` mit derselben URL rendert also sofort.
 *
 * Bereits erfolgreich geladene URLs werden synchron aufgelöst.
 */
export function loadImageQueued(url: string, signal?: AbortSignal): Promise<string> {
  const cached = succeeded.get(url)
  if (cached) return Promise.resolve(cached)

  const running = inProgress.get(url)
  if (running) return running

  const promise = new Promise<string>((resolve, reject) => {
    queue.push({
      url,
      key: url,
      attempts: 0,
      triedOriginal: false,
      signal,
      resolve,
      reject,
    })
  })
  inProgress.set(url, promise)
  // Verhindert unbehandelte Rejections, wenn nur ein Aufrufer sich anhängt.
  promise.catch(() => {})
  pump()
  return promise
}

/** True, wenn diese URL in dieser Session schon geladen wurde (kein Skeleton nötig). */
export function isImageCached(url: string): boolean {
  return succeeded.has(url)
}

/**
 * Wartezeit, die ein Retry außerhalb der Queue einhalten sollte — damit auch rohe
 * `<img>`-Tags (siehe `fallbackToOriginalOnError`) den globalen Cooldown respektieren,
 * statt mitten in die Sperre hineinzuwiederholen.
 */
export function currentCooldownMs(): number {
  return Math.max(0, pausedUntil - Date.now())
}

/**
 * Meldet den Fehler eines rohen `<img>`-Tags an den globalen Cooldown und liefert die
 * Wartezeit, die der Aufrufer vor dem nächsten Versuch einhalten sollte.
 */
export function noteImageFailure(): number {
  registerFailure()
  return currentCooldownMs()
}
